import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/app/services/user/user.service';
import { PlanService } from '../plan.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import * as alertifyjs from 'alertifyjs';
import { Router } from '@angular/router';

interface PlanMetrics {
  totalPlanes: number;
  planesActivos: number;
  planesCredito: number;
  clientesActivos: number;
  nuevosEstesMes: number;
  porcentajeActivos: number;
  promedioQuincenal: number;
  crecimientoMensual: number;
  proximosVencer: number;
}

/**
 * 🔴 LOS FILTROS SE REHICIERON EL 2026-08-21, Y ESTAS SON LAS MEDICIONES QUE LO
 * DECIDIERON (57 planes en la base de calidad):
 *
 *   · `visibility`: 57 de 57 son PUBLIC. El filtro «público / privado» no
 *     separaba NADA. Se fue.
 *   · `params.priority`: varía (30 en «0», 17 en «10») pero el negocio no lo
 *     usa. Se fue.
 *   · `params.type`: CREDITO 25 · CONTADO 21 · SUSCRIPCION 8 · sin tipo 3.
 *     Ese es el eje real por el que se busca un plan, y no estaba.
 *   · 34 planes tienen fecha de fin y 23 no; 25 tienen etiquetas; 3 están
 *     acotados por tienda y 1 por comercio.
 *
 * 🔴 Y el hallazgo grande: `filterProductType` arrancaba en 'CREDIT' y se manda
 * AL SERVIDOR, así que la pantalla mostraba 27 de 57 planes y el contador decía
 * «27 de 27». Más de la mitad del catálogo estaba escondido y nada lo decía.
 * Ahora se cargan todos y el producto es un filtro más, opcional.
 */
interface PlanFilters {
  // Siempre a la vista
  searchTerm: string;
  tipo: string;            // '' | 'CREDITO' | 'CONTADO' | 'SUSCRIPCION' | 'SIN_TIPO'
  filterEstado: string;    // '' | 'active' | 'inactive'

  // Los que se agregan desde «Agregar filtro». '' = sin usar.
  comercio: string;        // '' | 'ACOTADOS' | 'TODOS' | <id o código del comercio>
  fecha: string;           // '' | 'hoy' | '7d' | '30d' | 'anio' | 'rango'
  fechaDesde: string;      // AAAA-MM-DD, sólo cuando fecha === 'rango'
  fechaHasta: string;
  producto: string;        // '' | CREDIT | SC_RCV | SM_AP | SM_F | SM_V | TUDOC
  vigencia: string;        // '' | 'vigentes' | 'vencidos' | 'sin_fin' | 'vencen30'
  etiquetas: string;       // '' | 'con' | 'sin'
  tienda: string;          // '' | 'acotados' | 'todas'

  // ⚠️ Se conservan porque otras partes del componente los leen. No se muestran.
  filterTipo: string;
  filterVisibilidad: string;
  filterPrioridad: string;
  filterProductType: string;
}

/** Lo que se puede agregar desde «Agregar filtro», en el orden en que se ofrece. */
export const FILTROS_OPCIONALES = [
  { clave: 'comercio',  label: 'Comercio',           icono: 'fa-building' },
  { clave: 'fecha',     label: 'Fecha de creación',  icono: 'fa-calendar' },
  { clave: 'vigencia',  label: 'Vigencia',           icono: 'fa-hourglass-half' },
  { clave: 'producto',  label: 'Producto',           icono: 'fa-box' },
  { clave: 'tienda',    label: 'Tienda',             icono: 'fa-store' },
  { clave: 'etiquetas', label: 'Etiquetas',          icono: 'fa-tags' },
];

@Component({
  selector: 'app-plans-dashboard',
  templateUrl: './plans-dashboard.component.html',
  styleUrls: ['./plans-dashboard.component.css']
})
export class PlansDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  plans: any[] = [];
  filteredPlanes: any[] = [];
  paginatedPlanes: any[] = [];
  plansLoading = false;

  // Metrics
  metrics: PlanMetrics = {
    totalPlanes: 0,
    planesActivos: 0,
    planesCredito: 0,
    clientesActivos: 0,
    nuevosEstesMes: 0,
    porcentajeActivos: 0,
    promedioQuincenal: 0,
    crecimientoMensual: 0,
    proximosVencer: 0
  };

  // Filters and search
  filters: PlanFilters = {
    searchTerm: '',
    tipo: '',
    filterEstado: '',
    comercio: '', fecha: '', fechaDesde: '', fechaHasta: '',
    producto: '', vigencia: '', etiquetas: '', tienda: '',
    filterTipo: '', filterVisibilidad: '', filterPrioridad: '',
    // 🔴 VACÍO, no 'CREDIT'. Ver la nota de arriba: con 'CREDIT' la pantalla
    // escondía 30 de 57 planes y decía «27 de 27».
    filterProductType: ''
  };

  /** Qué filtros opcionales están puestos en pantalla. Arranca sin ninguno. */
  filtrosPuestos: string[] = [];
  mostrarMenuFiltros = false;
  readonly FILTROS_OPCIONALES = FILTROS_OPCIONALES;

  /** El catálogo de comercios, para el filtro por comercio. */
  comercios: any[] = [];

  // Filtro de tipo de producto
  productArray: any[] = []; // Lista de productos disponibles
  showProductDropdown: boolean = false; // Visibilidad del dropdown de productos
  isLoadingProducts: boolean = false; // Estado de carga de productos

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalPages = 0;
  startIndex = 0;
  endIndex = 0;
  visiblePages: number[] = [];

  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // View state
  viewMode: 'list' | 'cards' = 'list'; // Default to list as requested

  // Sistema de ayudas
  showHelp: { [key: string]: boolean } = {};
  helpData: any = {};

  constructor(
    private userService: UserService,
    private planService: PlanService,
    private offcanvasService: NgbOffcanvas,
    public authService: AuthService,
    private router: Router,
    private merchantService: MerchantService
  ) {}

  ngOnInit(): void {
    this.loadProductTypes(); // Cargar tipos de productos
    this.cargarComercios();  // Catálogo para el filtro por comercio
    this.loadPlansData();
    // ELIMINADO: this.setupRealTimeUpdates(); - Timer innecesario que causaba llamadas cada 30s
    this.loadHelpData(); // Cargar ayudas
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load plans data and calculate metrics
   */
  async loadPlansData(showLoading = true): Promise<void> {
    try {
      this.plansLoading = showLoading;

      // Load plans from API with optimized parameters
      // ⚡ OPTIMIZACIÓN: Agregamos 'lean: true' para que el backend no popule campos innecesarios
      // Esto reduce significativamente el tamaño de la respuesta y mejora la velocidad
      // No enviamos el parámetro status para traer TODOS los planes
      // 🔴 SIN `product_type`: se traen TODOS los planes. Antes se mandaba
      // 'CREDIT' por omisión y el servidor devolvía 27 de 57 — el resto no
      // existía para esta pantalla y el contador decía «27 de 27». El filtro por
      // producto sigue estando, pero ahora es opcional y del lado del cliente,
      // que con 57 planes es de sobra.
      const plansResponse = await this.userService.getplansall({
        lean: true  // Solicitar solo campos esenciales sin populate
      });
      
      // ⚡ OPTIMIZACIÓN: Eliminada llamada a getplansInvoices() que relentizaba la carga
      // El conteo de créditos ahora se carga solo en la vista de detalle del plan

      // Process plans data
      this.plans = plansResponse?.res?.map((plan: any) => {
        return {
          ...plan,
          // Add version info if not present
          version: plan.version || { major: 1, minor: 0 },
          // Calculate estimated audience (mock calculation)
          estimatedAudience: this.calculateEstimatedAudience(plan)
        };
      }) || [];

      // Calculate metrics
      this.calculateMetrics();

      // 🔴 Orden por omisión: EL ÚLTIMO CREADO PRIMERO. Antes no había ninguno
      // y los planes salían en el orden en que los devuelve la base, que para
      // quien entra a ver «qué se cargó» es el orden menos útil de todos.
      this.sortField = 'date';
      this.sortDirection = 'desc';

      // Apply initial filters
      this.applyFilters();

    } catch (error) {
      console.error('Error loading plans:', error);
      alertifyjs.error('Error al cargar los planes');
    } finally {
      this.plansLoading = false;
    }
  }

  /**
   * Calculate dashboard metrics
   */
  private calculateMetrics(): void {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    this.metrics = {
      totalPlanes: this.plans.length,
      planesActivos: this.plans.filter(p => p.status === true).length,
      // ⚠️ `params.category` NO EXISTE en ningún plan (verificado: 0 de 57), así
      // que esta métrica devolvía 0 siempre. Hoy no se muestra en ninguna parte;
      // se arregla igual para que el día que se muestre diga la verdad.
      planesCredito: this.plans.filter(p => this.tipoDe(p) === 'CREDITO').length,
      clientesActivos: 0, // Ya no se calcula aquí para mejorar rendimiento
      nuevosEstesMes: this.plans.filter(p => new Date(p.date) >= thisMonth).length,
      porcentajeActivos: this.plans.length > 0 ? Math.round((this.plans.filter(p => p.status === true).length / this.plans.length) * 100) : 0,
      promedioQuincenal: this.calculateAverageQuincenal(),
      crecimientoMensual: 12, // Mock data - would be calculated from historical data
      proximosVencer: this.plans.filter(p => {
        if (!p.params?.end_date) return false;
        const endDate = new Date(p.params.end_date);
        return endDate >= now && endDate <= nextMonth;
      }).length
    };
  }

  /**
   * Calculate average quincenal amount
   */
  private calculateAverageQuincenal(): number {
    const quincenalPlans = this.plans.filter(p =>
      p.payment_terms?.some((term: any) => term.frequency?.type === 'quincenal')
    );

    if (quincenalPlans.length === 0) return 0;

    const totalAmount = quincenalPlans.reduce((sum, plan) => {
      const amount = plan.params?.max_amount_base || plan.params?.min_amount_base || 0;
      return sum + amount;
    }, 0);

    return Math.round(totalAmount / quincenalPlans.length);
  }

  /**
   * Calculate estimated audience for a plan
   */
  private calculateEstimatedAudience(plan: any): string {
    // Mock calculation based on filters and plan type
    let baseAudience = 1000;

    if (plan.visibility === 'PRIVATE') baseAudience *= 0.3;
    if (this.tipoDe(plan) === 'CREDITO') baseAudience *= 0.7;
    if (plan.params?.filters?.general?.invoices?.shop?.type === 'white_list') {
      baseAudience *= 0.5;
    }

    return `~${Math.round(baseAudience).toLocaleString()} clientes`;
  }

  /**
   * ELIMINADO: setupRealTimeUpdates()
   * Ya no necesitamos timer automático - solo cargar cuando se accede al módulo
   */

  /**
   * Search functionality
   */
  onSearch(): void {
    this.applyFilters();
  }

  /**
   * Filter change handler
   */
  onFilterChange(): void {
    this.applyFilters();
  }

  /**
   * Apply all filters and search
   */
  applyFilters(): void {
    let filtered = [...this.plans];

    // Search filter
    if (this.filters.searchTerm.trim()) {
      const searchTerm = this.filters.searchTerm.toLowerCase();
      filtered = filtered.filter(plan =>
        plan.code?.toLowerCase().includes(searchTerm) ||
        plan.name?.toLowerCase().includes(searchTerm) ||
        plan.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (this.filters.filterEstado) {
      filtered = filtered.filter(plan => {
        if (this.filters.filterEstado === 'active') {
          return plan.status === true;
        } else if (this.filters.filterEstado === 'inactive') {
          return plan.status !== true;
        }
        return true;
      });
    }

    // ── Tipo de plan: crédito / contado / suscripción ─────────────────────
    if (this.filters.tipo) {
      filtered = filtered.filter(plan => this.tipoDe(plan) === this.filters.tipo);
    }

    // ── Comercio ──────────────────────────────────────────────────────────
    // 🔴 «ACOTADOS» y «TODOS» no son dos comercios: son las dos respuestas a
    // «¿este plan está limitado a alguien?». Un plan SIN lista aplica a todos
    // los comercios — esa es la regla del negocio, no un dato faltante.
    if (this.filters.comercio) {
      filtered = filtered.filter(plan => {
        const lista = this.comerciosDelPlan(plan);
        if (this.filters.comercio === 'ACOTADOS') return lista.length > 0;
        if (this.filters.comercio === 'TODOS') return lista.length === 0;
        // Un comercio concreto: le aplica si está nombrado, o si el plan no
        // nombra a ninguno (porque entonces le aplica a todos, él incluido).
        return lista.length === 0 || lista.some((c: any) => `${c}` === this.filters.comercio);
      });
    }

    // ── Fecha de creación ─────────────────────────────────────────────────
    if (this.filters.fecha) {
      const desde = this.desdeDelAtajo(this.filters.fecha);
      const hasta = this.hastaDelAtajo(this.filters.fecha);
      filtered = filtered.filter(plan => {
        const d = plan.date ? new Date(plan.date) : null;
        if (!d || isNaN(d.getTime())) return false;
        if (desde && d < desde) return false;
        if (hasta && d > hasta) return false;
        return true;
      });
    }

    // ── Vigencia ──────────────────────────────────────────────────────────
    if (this.filters.vigencia) {
      const hoy = new Date();
      const en30 = new Date(hoy.getTime() + 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(plan => {
        const fin = plan.params?.end_date ? new Date(plan.params.end_date) : null;
        switch (this.filters.vigencia) {
          case 'sin_fin':   return !fin;
          case 'vencidos':  return !!fin && fin < hoy;
          case 'vigentes':  return !fin || fin >= hoy;
          case 'vencen30':  return !!fin && fin >= hoy && fin <= en30;
          default: return true;
        }
      });
    }

    // ── Producto ──────────────────────────────────────────────────────────
    if (this.filters.producto) {
      filtered = filtered.filter(plan => plan.params?.product_type === this.filters.producto);
    }

    // ── Tienda ────────────────────────────────────────────────────────────
    if (this.filters.tienda) {
      filtered = filtered.filter(plan => {
        const n = plan.params?.filters?.general?.invoices?.shop?.list?.length || 0;
        return this.filters.tienda === 'acotados' ? n > 0 : n === 0;
      });
    }

    // ── Etiquetas ─────────────────────────────────────────────────────────
    if (this.filters.etiquetas) {
      filtered = filtered.filter(plan => {
        const n = plan.params?.filters?.dinamic_code?.person?.list?.length || 0;
        return this.filters.etiquetas === 'con' ? n > 0 : n === 0;
      });
    }

    this.filteredPlanes = filtered;
    this.ordenarFiltrados();
    this.currentPage = 1;
    this.updatePagination();
  }

  // ── Lo que necesitan los filtros y la tabla ─────────────────────────────

  /**
   * El tipo del plan. `params.type` viene en mayúsculas de la base; un plan sin
   * tipo (hay 3) NO se cuenta como crédito: se dice que no tiene.
   */
  tipoDe(plan: any): string {
    const t = String(plan?.params?.type || '').toUpperCase();
    return t === 'CREDITO' || t === 'CONTADO' || t === 'SUSCRIPCION' ? t : 'SIN_TIPO';
  }

  etiquetaTipo(plan: any): string {
    return ({ CREDITO: 'Crédito', CONTADO: 'Contado', SUSCRIPCION: 'Suscripción' } as any)[this.tipoDe(plan)]
      || 'Sin tipo';
  }

  /** Los comercios a los que está acotado el plan. Vacío = aplica a todos. */
  comerciosDelPlan(plan: any): any[] {
    return plan?.params?.filters?.general?.invoices?.merchant?.list || [];
  }

  /** Cuántos planes hay de cada tipo, para poder ponerlo dentro del botón. */
  cuantosDeTipo(tipo: string): number {
    return tipo ? this.plans.filter(p => this.tipoDe(p) === tipo).length : this.plans.length;
  }

  private desdeDelAtajo(atajo: string): Date | null {
    const hoy = new Date();
    const aMedianoche = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
    switch (atajo) {
      case 'hoy':  return aMedianoche(new Date());
      case '7d':   return aMedianoche(new Date(hoy.getTime() - 6 * 24 * 60 * 60 * 1000));
      case '30d':  return aMedianoche(new Date(hoy.getTime() - 29 * 24 * 60 * 60 * 1000));
      case 'anio': return new Date(hoy.getFullYear(), 0, 1);
      case 'rango': return this.filters.fechaDesde ? new Date(`${this.filters.fechaDesde}T00:00:00`) : null;
      default: return null;
    }
  }

  private hastaDelAtajo(atajo: string): Date | null {
    // ⚠️ Hasta el FINAL del día, no las 00:00: si no, «hoy» no encuentra nada
    // de lo cargado hoy, que es el caso más obvio y el más fácil de romper.
    if (atajo !== 'rango') return null;
    return this.filters.fechaHasta ? new Date(`${this.filters.fechaHasta}T23:59:59`) : null;
  }

  /** Aplica el orden vigente. Se llama al filtrar para que no se pierda. */
  private ordenarFiltrados(): void {
    if (!this.sortField) return;
    this.filteredPlanes.sort((a, b) => {
      const av = this.getFieldValue(a, this.sortField);
      const bv = this.getFieldValue(b, this.sortField);
      if (av < bv) return this.sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // ── «Agregar filtro» ────────────────────────────────────────────────────
  // 🔴 No se muestran los ocho filtros de entrada: se agregan de a uno. Una
  // barra con todo puesto obliga a leer ocho controles para usar uno, y el que
  // no se usa igual ocupa lugar y confunde. Los que están siempre son los tres
  // que se usan siempre: buscar, tipo y estado.

  get filtrosDisponibles(): any[] {
    return this.FILTROS_OPCIONALES.filter(f => !this.filtrosPuestos.includes(f.clave));
  }

  agregarFiltro(clave: string): void {
    if (!this.filtrosPuestos.includes(clave)) this.filtrosPuestos.push(clave);
    this.mostrarMenuFiltros = false;
  }

  quitarFiltro(clave: string): void {
    this.filtrosPuestos = this.filtrosPuestos.filter(c => c !== clave);
    // Sacar el filtro de la pantalla tiene que SACARLO de verdad: si se deja el
    // valor puesto, la lista sigue recortada por algo que ya no se ve.
    (this.filters as any)[clave] = '';
    if (clave === 'fecha') { this.filters.fechaDesde = ''; this.filters.fechaHasta = ''; }
    this.applyFilters();
  }

  etiquetaDelFiltro(clave: string): string {
    return this.FILTROS_OPCIONALES.find(f => f.clave === clave)?.label || clave;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '', tipo: '', filterEstado: '',
      comercio: '', fecha: '', fechaDesde: '', fechaHasta: '',
      producto: '', vigencia: '', etiquetas: '', tienda: '',
      filterTipo: '', filterVisibilidad: '', filterPrioridad: '', filterProductType: ''
    };
    // Los filtros agregados también se van: limpiar es volver al estado inicial,
    // no dejar seis controles vacíos ocupando la barra.
    this.filtrosPuestos = [];
    this.applyFilters();
    // ⚠️ Ya NO se recarga del servidor: los planes se traen todos una sola vez y
    // el filtrado es en memoria. Recargar acá era lo que reponía el 'CREDIT'.
  }

  /** El catálogo de comercios, para el filtro por comercio. */
  cargarComercios = async (): Promise<void> => {
    try {
      const rep: any = await this.merchantService.getMerchants({});
      this.comercios = (rep?.merchants || []).map((c: any) => ({
        id: c._id, code: c.code, name: c.name || c.business_name || c.code
      }));
    } catch (error) {
      // Sin catálogo el filtro por comercio queda con las dos opciones generales
      // («acotados» / «aplican a todos»), que son las que más se usan.
      console.error('No se pudieron cargar los comercios', error);
      this.comercios = [];
    }
  };

  /** El nombre de un comercio a partir de la clave guardada en el plan. */
  nombreComercio(clave: any): string {
    const c = this.comercios.find(x => `${x.id}` === `${clave}` || `${x.code}` === `${clave}`);
    return c ? c.name : `${clave}`;
  }

  /**
   * Sort functionality
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.ordenarFiltrados();
    this.updatePagination();
  }

  /**
   * Get field value for sorting
   */
  private getFieldValue(obj: any, field: string): any {
    switch (field) {
      case 'code': return obj.code || '';
      case 'name': return obj.name || '';
      // 🔴 Antes leía `params.category`, que NO EXISTE en ningún plan: ordenar
      // por tipo no hacía nada y no daba error. El tipo vive en `params.type`.
      case 'type': return this.tipoDe(obj);
      case 'status': return obj.status ? 'active' : 'inactive';
      case 'date': return new Date(obj.date || 0);
      case 'priority': return obj.params?.priority || 0;
      default: return '';
    }
  }

  /**
   * Pagination functionality
   */
  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredPlanes.length / this.pageSize);
    this.startIndex = (this.currentPage - 1) * this.pageSize;
    this.endIndex = Math.min(this.startIndex + this.pageSize, this.filteredPlanes.length);

    this.paginatedPlanes = this.filteredPlanes.slice(this.startIndex, this.endIndex);
    this.updateVisiblePages();
  }

  private updateVisiblePages(): void {
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    this.visiblePages = [];
    for (let i = start; i <= end; i++) {
      this.visiblePages.push(i);
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  /**
   * Plan actions
   */
  verPlan(plan: any): void {
    // Navigate to detailed view (Module 2)
    this.router.navigate(['/console/plans/detail', plan.code]);
  }

  /**
   * Editar plan
   */
  editarPlan(plan: any): void {
    this.router.navigate(['/console/plans/edit', plan.code]);
  }

  /**
   * Borrar plan
   */
  async borrarPlan(plan: any): Promise<void> {
    try {
      debugger
      // Llamar al servicio para borrar el plan (usar _id como prioridad, luego code)
      const planId = plan._id;
      const response = await this.planService.deletePlan(planId);

      if (response && response.res === true) {
        alertifyjs.success(`Plan "${plan.name}" borrado exitosamente`);
        // Recargar la lista de planes
        await this.loadPlansData();
      } else {
        alertifyjs.error('Error al borrar el plan');
      }
    } catch (error: any) {
      console.error('Error al borrar plan:');

      // Manejar errores específicos del backend
      if (error?.code === 1 && error?.msg) {
        alertifyjs.error(error.msg); // "Créditos existentes con este Plan"
      } else if (error?.error?.msg) {
        alertifyjs.error(`Error al borrar el plan`);
      } else if (error?.message) {
        alertifyjs.error(`Error al borrar el plan`);
      } else {
        alertifyjs.error('Error al borrar el plan. Verifica tu conexión e intenta nuevamente.');
      }
    }
  }

  async duplicarPlan(plan: any): Promise<void> {
    try {
      // Create a copy of the plan with new code
      const duplicatedPlan = {
        ...plan,
        code: `${plan.code}_COPY_${Date.now()}`,
        name: `${plan.name} (Copia)`,
        date: new Date().toISOString(),
        version: { major: 1, minor: 0 }
      };

      delete duplicatedPlan._id;
      delete duplicatedPlan.estimatedAudience;

      await this.planService.createPlan(duplicatedPlan).toPromise();
      alertifyjs.success('Plan duplicado exitosamente');
      this.loadPlansData(false);

    } catch (error) {
      console.error('Error duplicating plan:', error);
      alertifyjs.error('Error al duplicar el plan');
    }
  }

  /**
   * Toggle plan status
   */
  async toggleEstado(plan: any): Promise<void> {
    try {
      const newStatus = !plan.status;
      const newState = newStatus ? 'ACTIVE' : 'INACTIVE';

      console.log(`🔄 Toggling plan status: ${plan.code} from ${plan.status} to ${newStatus}`);

      // Get the complete plan object first
      const planResponse = await this.planService.getPlanByCode(plan.code).toPromise();

      console.log('📡 Plan response from API:', planResponse);

      if (!planResponse || !planResponse.res) {
        throw new Error('No se pudo obtener el plan completo');
      }

      // Update only the status and state fields in the complete plan object
      const completePlan = { ...planResponse.res };
      completePlan.status = newStatus;
      completePlan.state = newState;

      console.log('📤 Sending updated plan to API:', {
        code: completePlan.code,
        status: completePlan.status,
        state: completePlan.state
      });

      // Send the complete plan object to the API
      const updateResponse = await this.planService.updatePlan(completePlan);

      console.log('📡 Update response:', updateResponse);

      const actionMessage = newStatus ? 'activado' : 'desactivado';
      alertifyjs.success(`Plan ${actionMessage} exitosamente`);

      // Update the plan status and state locally
      plan.status = newStatus;
      plan.state = newState;

      // Recalculate metrics with updated data
      this.calculateMetrics();
      this.applyFilters();

    } catch (error: any) {
      console.error('❌ Error toggling plan status:', error);

      // More detailed error logging
      if (error.error) {
        console.error('❌ API Error details:', error.error);
      }

      const errorMessage = error?.error?.msg ||
                          error?.error?.message ||
                          error?.message ||
                          'Error al cambiar estado del plan';

      alertifyjs.error(errorMessage);
    }
  }

  /**
   * Create new plan
   */
  crearNuevoPlan(): void {
    this.router.navigate(['/console/plans/new']);
  }

  /**
   * Track by function for ngFor performance
   */
  trackByPlanId(index: number, plan: any): any {
    return plan._id || plan.code || index;
  }

  /**
   * Get status badge classes
   */
  getStatusBadgeClass(status: boolean): string {
    return status
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  }

  /**
   * Get category badge classes
   */
  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'CONTADO': return 'bg-green-100 text-green-800';
      case 'CREDITO': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: string | Date): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Get version display string
   */
  getVersionString(version: any): string {
    if (!version) return 'v1.0';
    return `v${version.major || 1}.${version.minor || 0}`;
  }

  /**
   * Get payment terms text for display
   */
  getPaymentTermsText(paymentTerms: any[]): string {
    if (!paymentTerms || paymentTerms.length === 0) return 'No definido';

    const terms = paymentTerms.map(term => {
      if (term.frequency?.type === 'quincenal') {
        return `${term.frequency.count || 4} cuotas quincenales`;
      } else if (term.frequency?.type === 'semanal') {
        return `${term.frequency.count || 4} cuotas semanales`;
      } else if (term.frequency?.type === 'personalizado') {
        return `${term.frequency.count || 2} cuotas personalizadas`;
      }
      return term.name || 'Término personalizado';
    });

    return terms.join(', ');
  }

  /**
   * Get quotas text for table display
   */
  getQuotasText(plan: any): string {
    if (!plan.payment_terms || plan.payment_terms.length === 0) return '1';

    const firstTerm = plan.payment_terms[0];
    if (firstTerm.installments) {
      return firstTerm.installments.toString();
    }

    return '1'; // Default
  }

  /**
   * Get frequency text for table display
   */
  getFrequencyText(plan: any): string {
    if (!plan.payment_terms || plan.payment_terms.length === 0) return 'Quincenal';

    const firstTerm = plan.payment_terms[0];
    if (firstTerm.frequency?.type === 'quincenal') {
      return 'Quincenal';
    } else if (firstTerm.frequency?.type === 'semanal') {
      return 'Semanal';
    } else if (firstTerm.frequency?.type === 'personalizado') {
      return 'Personalizado';
    }

    return firstTerm.name || 'Quincenal';
  }

  /**
   * Get priority text for table display
   */
  getPriorityText(plan: any): string {
    const priority = plan.params?.priority || 0;
    return priority.toString();
  }

  /**
   * Get created by display name with capitalization
   * Optimizado para manejar casos donde user no está poblado
   */
  getCreatedByDisplayName(plan: any): string {
    const user = plan.created_by?.user;
    
    // Si user es un ObjectId string en lugar de un objeto poblado
    if (typeof user === 'string') return '-';
    
    if (!user || (!user.name && !user.lastName)) return '-';

    const name = this.capitalizeFirstLetter(user.name || '');
    const lastName = this.capitalizeFirstLetter(user.lastName || '');

    return `${name} ${lastName}`.trim() || '-';
  }

  /**
   * Get updated by display name with capitalization
   * Optimizado para manejar casos donde user no está poblado
   */
  getUpdatedByDisplayName(plan: any): string {
    const user = plan.updated_by?.user;
    
    // Si user es un ObjectId string en lugar de un objeto poblado
    if (typeof user === 'string') return '-';
    
    if (!user || (!user.name && !user.lastName)) return '-';

    const name = this.capitalizeFirstLetter(user.name || '');
    const lastName = this.capitalizeFirstLetter(user.lastName || '');

    return `${name} ${lastName}`.trim() || '-';
  }

  /**
   * Capitalize first letter of a string
   */
  private capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  // Sistema de ayudas
  toggleHelp(section: string): void {
    this.showHelp[section] = !this.showHelp[section];
  }

  async loadHelpData(): Promise<void> {
    this.helpData = {
      'dashboard': {
        title: '¿Cómo usar el Dashboard de Planes?',
        icon: 'fas fa-info-circle',
        color: 'blue',
        description: 'Panel principal para gestionar todos los planes de crédito de KREDIT.',
        sections: [
          {
            title: 'Métricas Principales',
            items: [
              { label: 'Total de Planes', description: 'Cantidad total de planes creados en el sistema' },
              { label: 'Planes Activos', description: 'Planes disponibles para los clientes' },
              { label: 'Planes Inactivos', description: 'Planes deshabilitados temporalmente' },
              { label: 'Próximos a Vencer', description: 'Planes que se desactivarán pronto' }
            ]
          },
          {
            title: 'Vistas Disponibles',
            items: [
              { label: 'Vista Lista', description: 'Tabla detallada con toda la información', icon: 'fas fa-list' },
              { label: 'Vista Tarjetas', description: 'Cards visuales con información resumida', icon: 'fas fa-th' }
            ]
          }
        ],
        tip: { icon: '💡', title: 'Tip', text: 'Usa los filtros para encontrar planes específicos rápidamente' }
      },
      'filtros': {
        title: '¿Cómo funcionan los Filtros?',
        icon: 'fas fa-info-circle',
        color: 'green',
        description: 'Herramientas para encontrar planes específicos según diferentes criterios.',
        sections: [
          {
            title: 'Tipos de Filtro',
            items: [
              { label: 'Búsqueda', description: 'Busca por nombre, código o descripción del plan' },
              { label: 'Estado', description: 'Filtra por planes activos o inactivos' },
              { label: 'Visibilidad', description: 'Planes públicos (todos) o privados (específicos)' },
              { label: 'Prioridad', description: 'Nivel de importancia del plan (0-3)' }
            ]
          }
        ],
        tip: { icon: '🔍', title: 'Búsqueda', text: 'Combina múltiples filtros para resultados más precisos' }
      },
      'acciones': {
        title: '¿Qué acciones puedo realizar?',
        icon: 'fas fa-info-circle',
        color: 'purple',
        description: 'Operaciones disponibles para gestionar los planes.',
        sections: [
          {
            title: 'Acciones Principales',
            items: [
              { label: 'Ver', description: 'Consultar detalles completos del plan', icon: 'fas fa-eye' },
              { label: 'Duplicar', description: 'Crear una copia del plan para modificar', icon: 'fas fa-copy' },
              { label: 'Activar/Desactivar', description: 'Cambiar disponibilidad del plan', icon: 'fas fa-toggle-on' }
            ]
          }
        ],
        tip: { icon: '⚡', title: 'Eficiencia', text: 'Duplica planes similares para ahorrar tiempo en configuración' }
      }
    };
  }

  // ========== MÉTODOS PARA FILTRO DE TIPO DE PRODUCTO ==========
  
  /**
   * Cargar lista de tipos de productos
   */
  async loadProductTypes(): Promise<void> {
    try {
      this.isLoadingProducts = true;
      const response = await this.userService.getTypesProductsInvoices();
      
      // El servicio devuelve un array de objetos con params.code
      const products = response?.resp || [];
      
      // La respuesta ya viene con la estructura correcta { params: { code, name } }
      this.productArray = products;
      
      // Asegurar que CREDIT esté en la lista
      if (!this.productArray.find((p: any) => p?.params?.code === 'CREDIT')) {
        this.productArray.unshift({
          params: {
            code: 'CREDIT',
            name: 'CREDIT'
          }
        });
      }
      
      console.log('✅ Productos cargados:', this.productArray.length);
      console.log('📦 Productos:', this.productArray.map((p: any) => p?.params?.code));
    } catch (error) {
      console.error('Error loading product types:', error);
      // Si falla, al menos tener CREDIT como opción por defecto
      this.productArray = [{
        params: {
          code: 'CREDIT',
          name: 'CREDIT'
        }
      }];
    } finally {
      this.isLoadingProducts = false;
    }
  }

  /**
   * Toggle dropdown de productos
   */
  toggleProductDropdown(): void {
    this.showProductDropdown = !this.showProductDropdown;
  }

  /**
   * Cerrar dropdown de productos
   */
  closeProductDropdown(): void {
    this.showProductDropdown = false;
  }

  /**
   * Seleccionar tipo de producto
   */
  selectProductType(productCode: string): void {
    this.filters.filterProductType = productCode;
    this.showProductDropdown = false;
    this.loadPlansData(); // Recargar planes con el nuevo filtro
  }

  /**
   * Obtener el texto del producto seleccionado
   */
  getSelectedProductText(): string {
    const selected = this.productArray.find((p: any) => p?.params?.code === this.filters.filterProductType);
    return selected?.params?.code || 'CREDIT';
  }

}
